import React, { createContext, useContext, useState, useCallback } from "react";

const CourseContext = createContext();

export function useCourse() {
  return useContext(CourseContext);
}

export function CourseProvider({ children }) {
  const [courses, setCourses] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(() => {
    // Restore selected course from localStorage on initial load
    try {
      const saved = localStorage.getItem("selectedCourse");
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      console.error("Failed to restore selected course:", e);
      return null;
    }
  });

  const [showJoinModal, setShowJoinModal] = useState(false);
  const openJoinModal = useCallback(() => setShowJoinModal(true), []);
  // closeJoinModal(force=false): if force=true, always close; otherwise, prevent closing
  // when the user has zero courses (we want to force them to join before dismissing)
  const closeJoinModal = useCallback((force = false) => {
    if (!force && Array.isArray(courses) && courses.length === 0) {
      // do not close the modal for new users with 0 courses
      return;
    }
    setShowJoinModal(false);
  }, [courses]);

  const resetCourseContext = useCallback(() => {
    setCourses(null);
    setSelectedCourse(null);
    setShowJoinModal(false);
  }, []);

  const selectCourse = (course) => {
    setSelectedCourse(course);
    localStorage.setItem("selectedCourse", JSON.stringify(course));
  };
  const coursesInitialized = courses !== null;

  // Side-effect: when join modal is open for a user with no courses, blur the page via body class
  // Keep the blur active as long as the modal is showing, regardless of courses changes
  React.useEffect(() => {
    try {
      if (typeof document !== 'undefined' && document.body) {
        if (showJoinModal) {
          document.body.classList.add('bb-blur');
        } else {
          document.body.classList.remove('bb-blur');
        }
      }
    } catch (err) {
      // ignore DOM errors in non-browser environments
    }
    // cleanup on unmount
    return () => {
      try { document.body.classList.remove('bb-blur'); } catch(e){}
    };
  }, [showJoinModal]);

  return (
    <CourseContext.Provider value={{ selectedCourse, selectCourse, courses, setCourses, resetCourseContext, 
    showJoinModal, openJoinModal, closeJoinModal, coursesInitialized }}>
      {children}
    </CourseContext.Provider>
  );
}
